import React from "react";
import { createPortal } from "react-dom";
import "./LookupTableDropdown.styles.scss";

export interface LookupTableDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  lookupValues: Record<string, string[]> | {};
  onSelect: (value: string) => void;
  position: {
    x: number;
    y: number;
    placement: "bottom" | "top";
  };
  className?: string;
  selectedValue?: string;
  onTabNavigation?: (shiftKey: boolean) => void;
  onEnterNavigation?: (shiftKey: boolean) => void;
}

export const LookupTableDropdown: React.FC<LookupTableDropdownProps> = ({
  isOpen,
  onClose,
  lookupValues,
  onSelect,
  position,
  className = "",
  selectedValue = "",
  onTabNavigation,
  onEnterNavigation,
}) => {
  const [hoveredRowIndex, setHoveredRowIndex] = React.useState<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const bodyRef = React.useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = React.useState<{ left: number; top: number }>({ left: position.x, top: position.y });
  const [headerPadRight, setHeaderPadRight] = React.useState<number>(0);
  const selectedRowRef = React.useRef<HTMLDivElement | null>(null);

  // Process lookup table data into rows for display
  const lookupTableData = React.useMemo(() => {
    if (!lookupValues || Object.keys(lookupValues).length === 0) return [];
    
    const lookupValuesRecord = lookupValues as Record<string, string[]>;
    const columnKeys = Object.keys(lookupValuesRecord);
    const firstColumnKey = columnKeys[0];
    
    if (!firstColumnKey || !lookupValuesRecord[firstColumnKey]) return [];
    
    // Create rows by combining values from all columns
    const firstColumnData = lookupValuesRecord[firstColumnKey];
    if (!firstColumnData) return [];
    
    return firstColumnData.map((_: string, index: number) => {
      const row: Record<string, string> = {};
      columnKeys.forEach(key => {
        row[key] = lookupValuesRecord[key]?.[index] || '';
      });
      return row;
    });
  }, [lookupValues]);

  // Initialize hovered index when opening
  React.useEffect(() => {
    if (!isOpen) return;
    const idx = Math.max(0, lookupTableData.findIndex(row => {
      const firstKey = Object.keys(row)[0];
      const firstVal = firstKey ? row[firstKey] : '';
      return selectedValue !== undefined && String(firstVal?.toUpperCase() || '') === String(selectedValue?.toUpperCase() || '');
    }));
    setHoveredRowIndex(idx >= 0 ? idx : 0);
  }, [isOpen, lookupTableData, selectedValue]);

  // Clamp the dropdown within the viewport and align to grid
  React.useLayoutEffect(() => {
    if (!isOpen) return;
    const el = containerRef.current;
    const padding = 8; // viewport padding
    if (el) {
      const rect = el.getBoundingClientRect();
      const width = rect.width || 360;
      const height = rect.height || 240;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      let left = position.x - width / 2;
      if (left < padding) left = padding;
      if (left + width + padding > vw) left = Math.max(padding, vw - width - padding);

      let top = position.y;
      if (position.placement === 'bottom') {
        if (top + height + padding > vh) {
          top = Math.max(padding, position.y - height - 8);
        }
      } else {
        top = position.y - height - 8;
        if (top < padding) {
          top = Math.min(vh - height - padding, position.y + 8);
        }
      }

      setCoords({ left, top });
    }
  }, [isOpen, position.x, position.y, position.placement, lookupTableData.length]);

  // Scroll selected row into view on open
  React.useEffect(() => {
    if (!isOpen) return;
    if (selectedRowRef.current && bodyRef.current) {
      const container = bodyRef.current;
      const row = selectedRowRef.current;
      const rowTop = row.offsetTop;
      const rowBottom = rowTop + row.offsetHeight;
      const viewTop = container.scrollTop;
      const viewBottom = viewTop + container.clientHeight;
      if (rowTop < viewTop) {
        container.scrollTop = rowTop - 8;
      } else if (rowBottom > viewBottom) {
        container.scrollTop = rowBottom - container.clientHeight + 8;
      }
    }
  }, [isOpen, selectedValue]);


  // Scroll hovered row into view during keyboard navigation
  React.useEffect(() => {
    if (!isOpen || hoveredRowIndex == null || !bodyRef.current) return;
    const rows = bodyRef.current.querySelectorAll('.lookup-table-dropdown__row');
    const row = rows[hoveredRowIndex] as HTMLElement | undefined;
    if (row) {
      const container = bodyRef.current;
      const rowTop = row.offsetTop;
      const rowBottom = rowTop + row.offsetHeight;
      const viewTop = container.scrollTop;
      const viewBottom = viewTop + container.clientHeight;
      if (rowTop < viewTop) {
        container.scrollTop = rowTop - 4;
      } else if (rowBottom > viewBottom) {
        container.scrollTop = rowBottom - container.clientHeight + 4;
      }
    }
  }, [isOpen, hoveredRowIndex]);

  // Handle row selection
  const handleRowSelection = React.useCallback((row: Record<string, string>) => {
    const firstColumnKey = Object.keys(row)[0];
    const firstColumnValue = firstColumnKey ? row[firstColumnKey] : '';
    onSelect(firstColumnValue || '');
    onClose();
  }, [onSelect, onClose]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen) {
        const target = event.target as Element;
        if (!target.closest('.lookup-table-dropdown')) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Keyboard navigation and selection (Enter/Escape)
  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || lookupTableData.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setHoveredRowIndex(prev => {
          const next = (prev ?? 0) + 1;
          return Math.min(next, lookupTableData.length - 1);
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setHoveredRowIndex(prev => {
          const next = (prev ?? 0) - 1;
          return Math.max(next, 0);
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        const idx = hoveredRowIndex ?? 0;
        const row = lookupTableData[idx];
        if (row) {
          handleRowSelection(row);
        }
        onEnterNavigation?.(e.shiftKey);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        onTabNavigation?.(e.shiftKey);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, hoveredRowIndex, lookupTableData, handleRowSelection, onClose, onTabNavigation, onEnterNavigation]);

  if (!isOpen || lookupTableData.length === 0) {
    return null;
  }

  const headerColumns = Object.keys(lookupTableData[0] || {});

  return createPortal(
    <div
      ref={containerRef}
      className={`lookup-table-dropdown ${className}`}
      style={{
        position: "fixed",
        left: coords.left,
        top: coords.top,
        zIndex: 1100,
      }}
      onMouseLeave={() => setHoveredRowIndex(null)}
    >
      <div className="lookup-table-dropdown__content">
        {/* Header */}
        <div className="lookup-table-dropdown__header" style={{ paddingRight: headerPadRight }}>
          {headerColumns.map((columnKey) => (
            <div key={columnKey} className="lookup-table-dropdown__header-cell" title={columnKey}>
              {columnKey}
            </div>
          ))}
        </div>
        {/* Data rows */}
        <div ref={bodyRef} className="lookup-table-dropdown__body">
          {lookupTableData.map((row: Record<string, string>, index: number) => {
            const firstKey = Object.keys(row)[0];
            const firstVal = firstKey ? row[firstKey] : '';
            const isSelected = selectedValue !== undefined && String(firstVal?.toUpperCase() || '') === String(selectedValue?.toUpperCase() || '');
            const isHovered = hoveredRowIndex === index;
            return (
            <div
              key={index}
              ref={isSelected ? selectedRowRef : null}
              className={`lookup-table-dropdown__row ${
                (isHovered ? 'hovered' : '') + (isSelected ? ' selected' : '')
              }`}
              onMouseEnter={() => setHoveredRowIndex(index)}
              onClick={() => handleRowSelection(row)}
            >
              {headerColumns.map((columnKey, cellIndex) => {
                const value = row[columnKey];
                return (
                  <div key={cellIndex} className="lookup-table-dropdown__cell" title={value}>
                    {value}
                  </div>
                );
              })}
            </div>
          )})}
        </div>
      </div>
    </div>,
    document.body
  );
};
